import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { FlexGrid, Button } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import StationPropType from '../../../utils/prop-types/station';
import styles from './styles.css';
import StationProfileMap from '../../maps/station-profile';
import RowWithLabel from '../../row-with-label';
import InputWithLabel from '../../input-with-header';
import Modal from '../../modal';
import { editStation } from '../../../actions';
import Notification from '../../notification';

const { api, logError } = require('../../../../lib/bputils').default;

function EditContent({
  station: stationProps,
  dispatchEditStation,
  willSave,
  onSaved,
  buttonClassName,
}) {
  const [station, setStation] = React.useState(stationProps);

  React.useEffect(() => {
    setStation(station);
  }, [stationProps]);


  const handleOnInputChange = (newInformation) => {
    setStation(_.mergeWith(station, newInformation));
  };

  const handleOnMapInfoBoxPositionChange = (position) => {
    setStation({ ...station, latitude: position.lat, longitude: position.lng });
  };

  const handleOnModalClose = () => {
    setStation(stationProps);
  };

  const handleOnSave = async () => {
    willSave();

    const stationToSend = Object.assign({}, station.address);
    // the swagger validation will return a 400 if we pass it the country (we don't want users to be able to overwrite)
    delete stationToSend.country;

    stationToSend.phone_number = station.phone_number;
    stationToSend.latitude = station.latitude;
    stationToSend.longitude = station.longitude;
    stationToSend.retailer_reference = station.retailer_reference;
    stationToSend.retailer_name = station.retailer_name;

    try {
      await api.put({
        path: `/stations/${station.id}/information`,
        data: stationToSend,
      });

      Notification.success('Station information updated');
      dispatchEditStation(station);
    } catch (err) {
      logError(err);
      Notification.error('There was an error updating station information. Please contact support.');
    }

    onSaved();
  };

  return (
    <Modal
      title="EDIT STATION INFORMATION"
      saveButtonText="Save Changes"
      onClose={handleOnModalClose}
      onSave={handleOnSave}
      button={(
        <Button
          className={buttonClassName}
          type="button"
          link
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      )}
    >
      <div className={styles.brandInfoWrapper}>
        <FlexGrid className={styles.gridContainer}>
          <FlexGrid.Column mobile={12} tablet={6} className={styles.gridColumn}>
            <div className={styles.container}>
              <FlexGrid.Column mobile={12} tablet={6}>
                <InputWithLabel
                  label="Retailer ID (Internal)"
                  editable
                  onChange={e => handleOnInputChange({ retailer_reference: e.currentTarget.value })}
                  value={station.retailer_reference}
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={6}>
                <InputWithLabel
                  label="Retailer Name"
                  editable
                  onChange={e => handleOnInputChange({ retailer_name: e.currentTarget.value })}
                  value={station.retailer_name}
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={12}>
                <InputWithLabel
                  label="Address"
                  editable
                  onChange={e => handleOnInputChange({ address: { address: e.currentTarget.value } })}
                  value={station.address.address}
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={6}>
                <RowWithLabel label="City, State" spaced>
                  {station.address.city}
                  {', '}
                  {station.address.state}
                </RowWithLabel>
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={6}>
                <InputWithLabel
                  label="Zip Code"
                  editable
                  onChange={e => handleOnInputChange({ address: { zip: e.currentTarget.value } })}
                  value={station.address.zip}
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={6}>
                <InputWithLabel
                  label="Alternate Street Name"
                  editable
                  onChange={e => handleOnInputChange({ address: { address_alias: e.currentTarget.value } })}
                  value={station.address.address_alias}
                  isOptional
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={6}>
                <InputWithLabel
                  label="Cross Street"
                  editable
                  onChange={e => handleOnInputChange({ address: { cross_street: e.currentTarget.value } })}
                  value={station.address.cross_street}
                  isOptional
                />
              </FlexGrid.Column>
              <FlexGrid.Column mobile={12} tablet={12}>
                <InputWithLabel
                  label="Phone Number"
                  editable
                  onChange={e => handleOnInputChange({ phone_number: e.currentTarget.value })}
                  value={station.phone_number}
                />
              </FlexGrid.Column>
            </div>
          </FlexGrid.Column>
          <FlexGrid.Column key="Map" mobile={12} tablet={6} className={styles.gridColumn}>
            <StationProfileMap
              station={station}
              editMode
              onMapInfoBoxPositionChange={handleOnMapInfoBoxPositionChange}
            />
          </FlexGrid.Column>
        </FlexGrid>
      </div>
    </Modal>
  );
}

EditContent.propTypes = {
  station: StationPropType,
  dispatchEditStation: PropTypes.func,
  willSave: PropTypes.func,
  onSaved: PropTypes.func,
  buttonClassName: PropTypes.string,
};

EditContent.defaultProps = {
  station: null,
  willSave: null,
  onSaved: null,
  dispatchEditStation: null,
  buttonClassName: '',
};

const mapDispatchToProps = dispatch => ({
  dispatchEditStation: stationEdit => dispatch(editStation(stationEdit)),
});

const mapStateToProps = state => ({
  station: state.station,
});

export default connect(mapStateToProps, mapDispatchToProps)(EditContent);
